// useBlogTask.js
// Custom hook that opens a real-time Firestore listener on a blog_tasks/{taskId} doc
// while a long-running backend callable is executing, and surfaces the latest
// progress message for display in the busy/working modal.
//
// Usage:
//   const { taskId, taskMsg, startTask, stopTask } = useBlogTask();
//
//   async function handleAction() {
//     const id = startTask();          // generates UUID, subscribes onSnapshot
//     try {
//       await someCallable({ ..., taskId: id });
//     } finally {
//       stopTask();                    // unsubscribes
//     }
//   }

import { useCallback, useRef, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import { taskRef } from "./blogFirebase";

export function useBlogTask() {
  const [taskMsg, setTaskMsg]   = useState("");
  const unsubRef                = useRef(null);
  const taskIdRef               = useRef(null);

  /**
   * Generate a new task ID, subscribe to its Firestore doc, and return the ID.
   * Any previously open subscription is cleaned up first.
   */
  const startTask = useCallback(() => {
    // Clean up any lingering subscription
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Generate a simple UUID-like ID (crypto.randomUUID available in all modern browsers)
    const id = crypto.randomUUID();
    taskIdRef.current = id;
    setTaskMsg("");

    // Subscribe to the task doc — backend writes progress messages to log[]
    const ref   = taskRef(id);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const log = snap.data()?.log;
      if (Array.isArray(log) && log.length > 0) {
        setTaskMsg(log[log.length - 1]);
      }
    }, (err) => {
      console.warn("[useBlogTask] snapshot error:", err.message);
    });

    unsubRef.current = unsub;
    return id;
  }, []);

  /**
   * Unsubscribe from the task doc.
   * Does NOT clear taskMsg — the last progress message stays visible until
   * the next startTask() call (which resets it). Clearing it here would wipe
   * the message before the user ever sees it, since stopTask() runs in the
   * finally block right as the callable resolves.
   */
  const stopTask = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    taskIdRef.current = null;
  }, []);

  return { taskMsg, startTask, stopTask };
}
